import React, { useState, useEffect } from "react";
import { Card, Button, Spinner, Modal, Form, Alert } from "react-bootstrap";
import { url } from "./url";
import { fetchWithAuth } from "./FetchWithAuth";

export default function MfaSettings() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [setupMode, setSetupMode] = useState(null);
    const [setupData, setSetupData] = useState(null);
    const [confirmCode, setConfirmCode] = useState("");
    const [backupCodes, setBackupCodes] = useState(null);
    const [disablePassword, setDisablePassword] = useState("");
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const fetchStatus = async () => {
        try {
            const res = await fetchWithAuth(`${url}/mfa/status`, { method: "GET", credentials: "include" });
            if (res.ok) {
                const data = await res.json();
                setStatus(data);
            }
        } catch (e) {
            setError("Could not load MFA status.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();
    }, []);

    const handleSetupAuthenticator = async () => {
        setError("");
        setSetupMode("authenticator");
        setSetupData(null);
        setConfirmCode("");
        setBackupCodes(null);
        try {
            const res = await fetchWithAuth(`${url}/mfa/setup/authenticator`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
            });
            if (!res.ok) throw new Error("Failed to start setup");
            const data = await res.json();
            setSetupData(data);
        } catch (e) {
            setError(e.message || "Failed to start authenticator setup");
            setSetupMode(null);
        }
    };

    const handleSetupEmail = async () => {
        setError("");
        setSubmitting(true);
        try {
            const res = await fetchWithAuth(`${url}/mfa/setup/email`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
            });
            if (!res.ok) throw new Error("Failed to enable");
            const data = await res.json();
            setBackupCodes(data.backup_codes || []);
            setSuccess("Email MFA enabled. Save your backup codes.");
            fetchStatus();
        } catch (e) {
            setError(e.message || "Failed to enable email MFA");
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmAuthenticator = async (e) => {
        e.preventDefault();
        if (!confirmCode.trim()) return;
        setSubmitting(true);
        setError("");
        try {
            const res = await fetchWithAuth(`${url}/mfa/setup/authenticator/confirm`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ code: confirmCode.replace(/\s/g, "") }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || "Invalid code");
            }
            const data = await res.json();
            setBackupCodes(data.backup_codes || []);
            setSuccess("Authenticator MFA enabled. Save your backup codes.");
            setSetupMode(null);
            setSetupData(null);
            setConfirmCode("");
            fetchStatus();
        } catch (e) {
            setError(e.message || "Verification failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDisableMfa = async (e) => {
        e.preventDefault();
        if (!disablePassword.trim()) return;
        setSubmitting(true);
        setError("");
        try {
            const res = await fetchWithAuth(`${url}/mfa/disable`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ password: disablePassword }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || "Failed to disable");
            }
            setSuccess("MFA disabled.");
            setShowDisableModal(false);
            setDisablePassword("");
            fetchStatus();
        } catch (e) {
            setError(e.message || "Failed to disable MFA");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" /> Loading MFA status...
            </div>
        );
    }

    return (
        <Card className="mb-4">
            <Card.Header>
                <Card.Title className="mb-0">Two-factor authentication (MFA)</Card.Title>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}
                {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible>{success}</Alert>}

                {status?.mfa_enabled ? (
                    <div>
                        <p className="text-muted">
                            MFA is enabled. Preferred method: <strong>{status.preferred_method || "—"}</strong>.
                            {status.has_authenticator && " Authenticator app is set up."}
                            {" "}{status.backup_codes_count ?? 0} backup codes remaining.
                        </p>
                        <Button variant="outline-danger" onClick={() => setShowDisableModal(true)}>
                            Disable MFA
                        </Button>
                    </div>
                ) : (
                    <div>
                        <p className="text-muted mb-3">Add an extra layer of security with email, authenticator app, or backup codes.</p>
                        <div className="d-flex flex-wrap gap-2">
                            <Button variant="primary" onClick={handleSetupAuthenticator} disabled={submitting}>
                                Enable with Microsoft Authenticator
                            </Button>
                            <Button variant="outline-primary" onClick={handleSetupEmail} disabled={submitting}>
                                {submitting ? <Spinner animation="border" size="sm" /> : "Enable with Email"}
                            </Button>
                        </div>
                    </div>
                )}

                {setupMode === "authenticator" && setupData && (
                    <div className="mt-4 p-3 border rounded">
                        <h6>Set up authenticator (email verification)</h6>
                        <p className="small text-muted">
                            Verify that the account shown in your authenticator app matches this email: <strong>{setupData.email}</strong>
                        </p>
                        <p className="small">Scan the QR code with Microsoft Authenticator, or enter the secret manually. The app will show your email so you can confirm it matches.</p>
                        <p className="small font-monospace">{setupData.secret}</p>
                        <Form onSubmit={handleConfirmAuthenticator}>
                            <Form.Group className="mb-2">
                                <Form.Label>Enter the 6-digit code from your app</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="000000"
                                    value={confirmCode}
                                    onChange={(e) => setConfirmCode(e.target.value)}
                                    maxLength={8}
                                />
                            </Form.Group>
                            <Button type="submit" variant="primary" disabled={submitting || confirmCode.length < 6}>
                                {submitting ? <Spinner animation="border" size="sm" /> : "Confirm and enable MFA"}
                            </Button>
                            <Button type="button" variant="link" className="ms-2" onClick={() => { setSetupMode(null); setSetupData(null); }}>
                                Cancel
                            </Button>
                        </Form>
                    </div>
                )}

                {backupCodes && backupCodes.length > 0 && (
                    <Alert variant="warning" className="mt-3">
                        <strong>Save these backup codes.</strong> Each can only be used once.
                        <pre className="mt-2 mb-0 small font-monospace" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                            {backupCodes.join("\n")}
                        </pre>
                        <Button size="sm" variant="outline-secondary" className="mt-2" onClick={() => setBackupCodes(null)}>Done</Button>
                    </Alert>
                )}
            </Card.Body>

            <Modal show={showDisableModal} onHide={() => setShowDisableModal(false)}>
                <Modal.Header closeButton><Modal.Title>Disable MFA</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleDisableMfa}>
                        <Form.Group>
                            <Form.Label>Enter your password to disable MFA</Form.Label>
                            <Form.Control
                                type="password"
                                value={disablePassword}
                                onChange={(e) => setDisablePassword(e.target.value)}
                                placeholder="Password"
                                required
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDisableModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleDisableMfa} disabled={submitting || !disablePassword}>
                        {submitting ? <Spinner animation="border" size="sm" /> : "Disable MFA"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Card>
    );
}
